import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/** Shared motion: lift on hover, icon nudge; prefers-reduced-motion safe. Active press scale is per-variant (cva does not merge conflicts). */
const buttonMotion =
  'transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out motion-reduce:transition-colors motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none motion-reduce:active:scale-100 motion-reduce:hover:[&_svg]:scale-100 ' +
  'shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm active:duration-100 ' +
  '[&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200 [&_svg]:ease-out hover:[&_svg]:scale-105 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ' +
  'disabled:pointer-events-none disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm disabled:active:scale-100 disabled:hover:[&_svg]:scale-100';

const buttonPress = 'active:scale-[0.98]';

export const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium leading-none cursor-pointer disabled:cursor-not-allowed touch-manipulation ${buttonMotion}`,
  {
    variants: {
      variant: {
        default: `${buttonPress} bg-primary text-white shadow-md shadow-gray-900/10 hover:bg-primary-600 hover:shadow-lg hover:shadow-gray-900/15 focus-visible:ring-primary`,
        primary: `${buttonPress} bg-primary text-white shadow-md shadow-gray-900/10 hover:bg-primary-600 hover:shadow-lg hover:shadow-gray-900/15 focus-visible:ring-primary`,
        destructive: `${buttonPress} bg-rose-600 text-white shadow-md shadow-rose-900/20 hover:bg-rose-700 hover:shadow-lg hover:shadow-rose-900/25 focus-visible:ring-rose-500`,
        outline: `${buttonPress} border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-muted-strong hover:bg-gray-50 dark:hover:bg-gray-700/95 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md focus-visible:ring-primary`,
        'outline-primary': `${buttonPress} border border-primary/90 dark:border-primary-500 bg-transparent text-primary dark:text-primary-400 hover:bg-primary/10 dark:hover:bg-primary/20 hover:border-primary dark:hover:border-primary-400 hover:shadow-md focus-visible:ring-primary`,
        secondary: `${buttonPress} border border-gray-300 bg-gray-100 text-foreground hover:border-gray-400 hover:bg-gray-200 focus-visible:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-50 dark:hover:border-gray-500 dark:hover:bg-gray-600`,
        ghost: `${buttonPress} shadow-none text-muted-strong hover:bg-gray-100/90 dark:hover:bg-gray-700/90 hover:text-foreground focus-visible:ring-primary hover:[&_svg]:scale-110`,
        link: 'shadow-none text-primary dark:text-primary-400 underline-offset-4 hover:underline hover:text-primary-600 dark:hover:text-primary-300 focus-visible:ring-primary hover:translate-y-0 hover:shadow-none active:scale-100 hover:[&_svg]:scale-100',
        success: `${buttonPress} bg-emerald-600 text-white shadow-md shadow-emerald-900/15 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-900/20 focus-visible:ring-emerald-500`,
        warning: `${buttonPress} bg-amber-500 text-white shadow-md shadow-amber-900/15 hover:bg-amber-600 hover:shadow-lg hover:shadow-amber-900/20 focus-visible:ring-amber-500`,
        info: `${buttonPress} bg-blue-600 text-white shadow-md shadow-blue-900/15 hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-900/20 focus-visible:ring-blue-500`,
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-lg px-8',
        xl: 'h-14 rounded-xl px-10 text-base', // Extra large for mobile
        icon: 'h-10 w-10 gap-0 p-0',
        'icon-sm': 'h-8 w-8 gap-0 p-0 rounded-md',
        'icon-lg': 'h-12 w-12 gap-0 p-0 rounded-xl',
        'icon-touch': 'h-12 w-12 gap-0 p-0 md:h-10 md:w-10 rounded-xl md:rounded-lg', // Touch-friendly icons
        'mobile-action': 'h-11 w-11 gap-0 p-0 md:h-8 md:w-8 rounded-xl md:rounded-lg', // Mobile action buttons
        'mobile-primary': 'h-12 px-6 py-3 text-base rounded-xl md:h-10 md:px-4 md:py-2 md:text-sm md:rounded-lg', // Mobile primary
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
    },
  },
);

export type IconButtonSize = 'icon-sm' | 'icon' | 'icon-lg' | 'icon-touch' | 'mobile-action';

/** Native `<button>` or extra classes — same look/motion as `<Button variant="ghost" size="…" />`. */
export function iconButtonClassName(size: IconButtonSize = 'icon', className?: string) {
  return cn(buttonVariants({ variant: 'ghost', size }), className);
}

/**
 * Dark navbar icon triggers (cart, notifications, mobile search/menu).
 * Flat on hover (no lift); press + icon motion aligned with ghost icon buttons.
 */
export const navbarGhostIconButtonClassName =
  'inline-flex items-center justify-center touch-manipulation cursor-pointer gap-0 ' +
  'transition-[transform,background-color,box-shadow] duration-200 ease-out ' +
  'hover:translate-y-0 active:scale-[0.98] motion-reduce:active:scale-100 ' +
  '[&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-200 hover:[&_svg]:scale-105 motion-reduce:hover:[&_svg]:scale-100 ' +
  'text-white hover:bg-dark-600 rounded-lg border-0 shadow-none hover:shadow-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-700 ' +
  'disabled:pointer-events-none disabled:opacity-50 disabled:hover:[&_svg]:scale-100';
